import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, FileText, TrendingUp, Crown, Search, Eye,
  AlertTriangle, ExternalLink, ShieldCheck,
  Globe, Mail, Webhook, FlaskConical, ScrollText, Check, X as XIcon
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat("en-US").format(n || 0);

const STATUS_BADGE = {
  healthy: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border border-amber-200",
  critical: "bg-orange-50 text-orange-700 border border-orange-200",
  maxed_out: "bg-red-50 text-red-700 border border-red-200",
  unlimited: "bg-purple-50 text-purple-700 border border-purple-200",
};

const LICENSE_BADGE = {
  active: "bg-emerald-50 text-emerald-700",
  trial: "bg-blue-50 text-blue-700",
  expired: "bg-red-50 text-red-700",
};

export default function OwnerConsole() {
  const [fleet, setFleet] = useState(null);
  const [burn, setBurn] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [forbidden, setForbidden] = useState(false);

  // Sprint 3 — Tab state + per-tab data (additive, lazy-loaded)
  const [tab, setTab] = useState("overview");
  const [emailLog, setEmailLog] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [disclaimers, setDisclaimers] = useState(null);
  const [logins, setLogins] = useState(null);
  const [testFlagSaving, setTestFlagSaving] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [f, b] = await Promise.all([
          axios.get(`${API}/owner/fleet`, { withCredentials: true }),
          axios.get(`${API}/owner/burn-rate`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setFleet(f.data); setBurn(b.data);
      } catch (err) {
        if (err.response?.status === 403) setForbidden(true);
      }
    };
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Sprint 3 — lazy fetch per tab; refetch when user re-opens a tab
  useEffect(() => {
    if (forbidden) return;
    if (tab === "logins" && logins === null) {
      axios.get(`${API}/owner/login-locations`, { withCredentials: true })
        .then(r => setLogins(r.data)).catch(() => setLogins({ rows: [], total: 0 }));
    } else if (tab === "email-log" && emailLog === null) {
      axios.get(`${API}/owner/email-log?limit=100`, { withCredentials: true })
        .then(r => setEmailLog(r.data)).catch(() => setEmailLog({ rows: [], total: 0 }));
    } else if (tab === "webhooks" && webhooks === null) {
      axios.get(`${API}/owner/webhooks?limit=100`, { withCredentials: true })
        .then(r => setWebhooks(r.data)).catch(() => setWebhooks({ rows: [], totals: {} }));
    } else if (tab === "disclaimers" && disclaimers === null) {
      axios.get(`${API}/owner/disclaimers`, { withCredentials: true })
        .then(r => setDisclaimers(r.data)).catch(() => setDisclaimers({ rows: [], total: 0, accepted: 0, pending: 0 }));
    }
  }, [tab, forbidden, logins, emailLog, webhooks, disclaimers]);

  const toggleTestFlag = async (userId, currentValue) => {
    setTestFlagSaving(s => ({ ...s, [userId]: true }));
    try {
      await axios.post(
        `${API}/admin/accounts/${userId}/test-flag`,
        { is_test_account: !currentValue },
        { withCredentials: true }
      );
      // Update the fleet row in place + refresh dependent tab data
      setFleet(f => f ? {
        ...f,
        customers: f.customers.map(c => c.user_id === userId ? { ...c, is_test_account: !currentValue } : c)
      } : f);
      if (logins) {
        setLogins(d => ({ ...d, rows: d.rows.map(r => r.user_id === userId ? { ...r, is_test_account: !currentValue } : r) }));
      }
      if (disclaimers) {
        setDisclaimers(d => ({ ...d, rows: d.rows.map(r => r.user_id === userId ? { ...r, is_test_account: !currentValue } : r) }));
      }
    } catch {
      // swallow — table will rehydrate on next interval
    } finally {
      setTestFlagSaving(s => ({ ...s, [userId]: false }));
    }
  };

  if (forbidden) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <ShieldCheck className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-red-900 mb-1" style={{ fontFamily: "Manrope" }}>Owner access required</h2>
          <p className="text-red-700 text-sm">This page is restricted to the account owner.</p>
        </div>
      </div>
    );
  }

  if (!fleet || !burn) {
    return <div className="p-8"><div className="h-64 bg-slate-100 rounded-xl animate-pulse" /></div>;
  }

  const customers = fleet.customers || [];
  const ownersHidden = customers.filter(c => c.role === "owner").length;
  const visible = customers.filter(c => c.role !== "owner");
  const filtered = visible.filter(c => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.center_name || "").toLowerCase().includes(q);
  });

  const totalCustomers = visible.length;
  const pagesThisMonth = burn.pages_this_month || 0;
  const avgPerCust = totalCustomers > 0 ? Math.round(pagesThisMonth / totalCustomers) : 0;
  const topCustomer = visible.length > 0
    ? visible.reduce((a, b) => (a.pages_used_year > b.pages_used_year ? a : b))
    : null;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
            <Crown className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Owner Console</h1>
            <p className="text-slate-500 text-sm">Your fleet overview and cost management. {ownersHidden > 0 && <span className="text-slate-400">({ownersHidden} owner account{ownersHidden !== 1 ? "s" : ""} hidden)</span>}</p>
          </div>
        </div>
      </div>

      {/* Sprint 3 — Tab navigation (additive) */}
      <div className="border-b border-slate-200 -mx-6 lg:-mx-8 px-6 lg:px-8" data-testid="owner-tabs-nav">
        <nav className="flex gap-1 overflow-x-auto -mb-px">
          <TabButton id="overview" active={tab} onClick={setTab} icon={TrendingUp} label="Overview" />
          <TabButton id="logins" active={tab} onClick={setTab} icon={Globe} label="IP & Geo" />
          <TabButton id="email-log" active={tab} onClick={setTab} icon={Mail} label="Email Log" />
          <TabButton id="webhooks" active={tab} onClick={setTab} icon={Webhook} label="Webhooks" />
          <TabButton id="test-accounts" active={tab} onClick={setTab} icon={FlaskConical} label="Test Accounts" />
          <TabButton id="disclaimers" active={tab} onClick={setTab} icon={ScrollText} label="Terms & Conditions" />
        </nav>
      </div>

      {tab === "overview" && (<>
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="owner-kpi-cards">
        <KpiCard icon={Users} label="Total Customers" value={fmtN(totalCustomers)} accent={{ bg: "bg-purple-50", text: "text-purple-600" }} testId="kpi-total-customers" />
        <KpiCard icon={FileText} label="Pages This Month" value={fmtN(pagesThisMonth)} accent={{ bg: "bg-blue-50", text: "text-blue-600" }} testId="kpi-pages-month" />
        <KpiCard icon={TrendingUp} label="Avg Pages / Customer" value={fmtN(avgPerCust)} accent={{ bg: "bg-amber-50", text: "text-amber-600" }} testId="kpi-avg-pages" />
        <KpiCard
          icon={Crown}
          label="Top Customer (Pages)"
          value={topCustomer ? topCustomer.center_name || topCustomer.name || "—" : "—"}
          subtitle={topCustomer ? `${fmtN(topCustomer.pages_used_year)} pages` : ""}
          accent={{ bg: "bg-purple-50", text: "text-purple-600" }}
          small
          testId="kpi-top-customer"
        />
      </div>

      {/* Fleet Usage table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="fleet-usage-card">
        <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Fleet Usage</h2>
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="fleet-search"
              placeholder="Search customers..."
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            data-testid="fleet-status-filter"
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="all">All Status</option>
            <option value="healthy">Healthy</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
            <option value="maxed_out">Maxed Out</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pages Used</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Quota</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-48">% Used</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Upload</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-400 text-sm">No customers match your filters.</td></tr>
              ) : filtered.map(c => (
                <tr key={c.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`fleet-row-${c.user_id}`}>
                  <td className="px-6 py-3">
                    <p className="font-semibold text-slate-900">{c.center_name || c.name || "—"}</p>
                    <p className="text-slate-500 text-xs">{c.email}</p>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${STATUS_BADGE[c.status] || STATUS_BADGE.healthy}`}>
                      {c.status === "maxed_out" ? "Maxed Out" : c.status[0].toUpperCase() + c.status.slice(1)}
                    </span>
                    {c.license_status === "trial" && (
                      <span className={`ml-1 text-xs px-2 py-0.5 rounded-md font-medium ${LICENSE_BADGE.trial}`}>Trial</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-700 text-xs">
                    {{
                      starter: "Starter ($99)",
                      pro: "Professional ($149)",
                      business: "Business ($249)",
                      trial: "Free Trial",
                    }[c.plan_tier] || c.plan_tier}
                  </td>
                  <td className="px-3 py-3 text-right text-slate-900 font-semibold">{fmtN(c.pages_used_year)}</td>
                  <td className="px-3 py-3 text-right text-slate-500">{fmtN(c.pages_quota_year)}{c.pages_purchased_extra > 0 && <span className="text-emerald-600 text-xs"> +{c.pages_purchased_extra}</span>}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden min-w-20">
                        <div
                          className={`h-full rounded-full ${
                            c.status === "maxed_out" ? "bg-red-500" :
                            c.status === "critical" ? "bg-orange-500" :
                            c.status === "warning" ? "bg-amber-400" :
                            "bg-emerald-500"
                          }`}
                          style={{ width: `${Math.min(c.percent_used, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-700 font-semibold w-12 text-right">{c.percent_used}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">
                    {c.last_upload ? formatRelative(c.last_upload) : "Never"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button data-testid={`fleet-view-${c.user_id}`} className="text-slate-400 hover:text-purple-600 transition-colors" title="View details">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom 3-column row: Universal Key / Burn Rate / Revenue + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Universal Key */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="universal-key-card">
          <h3 className="font-bold text-slate-900 text-sm mb-3" style={{ fontFamily: "Manrope" }}>Emergent Universal Key</h3>
          <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-1">Burn Rate</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{fmt(burn.cost_to_date)}</p>
          <p className="text-xs text-emerald-600 mt-1">~{fmtN(pagesThisMonth)} pages this month</p>
          <a
            href="https://app.emergent.sh/profile"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topup-universal-key-link"
            className="mt-4 w-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all"
          >
            Top Up Universal Key <ExternalLink className="w-4 h-4" />
          </a>
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            <span>Enable auto-recharge in your Emergent profile</span>
          </div>
        </div>

        {/* Burn Rate chart */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="burn-rate-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Burn Rate ({burn.month_label})</h3>
            <span className="text-xs text-slate-400">Est: {fmt(burn.est_month_end)}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Cost to Date</p>
              <p className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{fmt(burn.cost_to_date)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Est. Month End</p>
              <p className="text-xl font-bold text-slate-700" style={{ fontFamily: "Manrope" }}>{fmt(burn.est_month_end)}</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={burn.daily_series}>
              <defs>
                <linearGradient id="burn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} hide />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} hide />
              <Tooltip formatter={(v, name) => name === "cost" ? fmt(v) : `${v} pages`} />
              <Area type="monotone" dataKey="cost" stroke="#7C3AED" fill="url(#burn)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue + Alerts */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="revenue-card">
            <h3 className="font-bold text-slate-900 text-sm mb-3" style={{ fontFamily: "Manrope" }}>Revenue (Page Packs)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">This Month</p>
                <p className="text-xl font-bold text-emerald-600" style={{ fontFamily: "Manrope" }}>{fmt(burn.revenue_month)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">This Year</p>
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{fmt(burn.revenue_year)}</p>
              </div>
            </div>
            {burn.top_seller && (
              <p className="text-xs text-slate-500 mt-2">Top seller: <span className="font-semibold text-slate-700">{burn.top_seller.sku}</span> ({burn.top_seller.count})</p>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="alerts-card">
            <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Alerts
            </h3>
            <ul className="space-y-1.5 text-sm">
              <AlertRow color="red" text={`${burn.alerts.over_90_pct} customer${burn.alerts.over_90_pct !== 1 ? "s" : ""} over 90% quota`} />
              <AlertRow color="orange" text={`${burn.alerts.maxed_out} customer${burn.alerts.maxed_out !== 1 ? "s" : ""} maxed out`} />
              <AlertRow color="amber" text={`${burn.alerts.trials_expiring_7d} trial${burn.alerts.trials_expiring_7d !== 1 ? "s" : ""} expiring in 7 days`} />
            </ul>
          </div>
        </div>
      </div>
      </>)}

      {tab === "logins" && (
        <LoginLocationsTab data={logins} />
      )}

      {tab === "email-log" && (
        <EmailLogTab data={emailLog} />
      )}

      {tab === "webhooks" && (
        <WebhooksTab data={webhooks} />
      )}

      {tab === "test-accounts" && (
        <TestAccountsTab
          customers={visible}
          saving={testFlagSaving}
          onToggle={toggleTestFlag}
        />
      )}

      {tab === "disclaimers" && (
        <DisclaimersTab data={disclaimers} />
      )}
    </div>
  );
}

function TabButton({ id, active, onClick, icon: Icon, label }) {
  const isActive = active === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={`owner-tab-${id}`}
      className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${
        isActive
          ? "border-purple-600 text-purple-700"
          : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function TabShell({ title, subtitle, children, count, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={testId}>
      <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {typeof count === "number" && (
          <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md font-medium">{fmtN(count)} {count === 1 ? "row" : "rows"}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ colSpan, text }) {
  return (
    <tr><td colSpan={colSpan} className="px-6 py-12 text-center text-slate-400 text-sm">{text}</td></tr>
  );
}

function LoadingShell({ testId }) {
  return <div className="h-64 bg-slate-100 rounded-xl animate-pulse" data-testid={testId} />;
}

function LoginLocationsTab({ data }) {
  if (!data) return <LoadingShell testId="logins-loading" />;
  const rows = data.rows || [];
  return (
    <TabShell title="IP & Geo (Last Login per User)" subtitle="Snapshot of each user's most recent successful login. Overwrites on each login — this is a current-state view, not a history log." count={data.total} testId="logins-tab-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Login</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">IP</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Timezone</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <EmptyRow colSpan={5} text="No login records yet." /> : rows.map(r => (
              <tr key={r.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`logins-row-${r.user_id}`}>
                <td className="px-6 py-3">
                  <p className="font-semibold text-slate-900 flex items-center gap-1.5">
                    {r.center_name || r.name || "—"}
                    {r.is_test_account && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">TEST</span>}
                  </p>
                  <p className="text-slate-500 text-xs">{r.email}</p>
                </td>
                <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">{r.last_login_at ? formatRelative(r.last_login_at) : "Never"}</td>
                <td className="px-3 py-3 text-slate-700 text-xs font-mono">{r.last_login_ip || "—"}</td>
                <td className="px-3 py-3 text-slate-700 text-xs">
                  {[r.last_login_city, r.last_login_region, r.last_login_country].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-3 py-3 text-slate-500 text-xs">{r.last_login_timezone || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabShell>
  );
}

function EmailLogTab({ data }) {
  if (!data) return <LoadingShell testId="email-log-loading" />;
  const rows = data.rows || [];
  return (
    <TabShell title="Email Log" subtitle="Tax-prep emails sent to accountants via Resend. Audit trail only — content not stored." count={data.total} testId="email-log-tab-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sent</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">From</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Recipients</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tax Year</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Attached</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <EmptyRow colSpan={6} text="No tax-prep emails have been sent yet." /> : rows.map((r, idx) => (
              <tr key={`${r.sent_at}-${idx}`} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`email-log-row-${idx}`}>
                <td className="px-6 py-3 text-slate-500 text-xs whitespace-nowrap">{r.sent_at ? formatRelative(r.sent_at) : "—"}</td>
                <td className="px-3 py-3">
                  <p className="text-slate-900 text-xs font-medium">{r.sender_center || r.sender_name || "—"}</p>
                  <p className="text-slate-500 text-xs">{r.sender_email || "—"}</p>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(r.recipients || []).slice(0, 3).map((e, i) => (
                      <span key={i} className="bg-slate-100 px-2 py-0.5 rounded text-[11px]">{e}</span>
                    ))}
                    {(r.recipients || []).length > 3 && <span className="text-slate-400 text-[11px]">+{r.recipients.length - 3}</span>}
                  </div>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs">{r.tax_year ?? "—"}</td>
                <td className="px-3 py-3 text-right text-slate-700 text-xs">{r.attached_count || 0}</td>
                <td className="px-3 py-3 text-xs">
                  <StatusPill status={r.status} />
                  {r.error && <p className="text-red-600 text-[11px] mt-0.5">{r.error}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabShell>
  );
}

function WebhooksTab({ data }) {
  if (!data) return <LoadingShell testId="webhooks-loading" />;
  const rows = data.rows || [];
  const totals = data.totals || {};
  return (
    <TabShell
      title="Square Webhooks"
      subtitle={`webhook_arrivals: ${fmtN(totals.webhook_arrivals || 0)} · square_events: ${fmtN(totals.square_events || 0)} (merged by time)`}
      count={rows.length}
      testId="webhooks-tab-card"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Received</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Event ID / Path</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Body / Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <EmptyRow colSpan={6} text="No webhook activity yet." /> : rows.map((r, idx) => (
              <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`webhooks-row-${idx}`}>
                <td className="px-6 py-3 text-slate-500 text-xs whitespace-nowrap">{r.received_at ? formatRelative(r.received_at) : "—"}</td>
                <td className="px-3 py-3 text-xs">
                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${r.source === "square_events" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
                    {r.source}
                  </span>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs font-mono break-all max-w-xs">{r.event_id || r.url_path || "—"}</td>
                <td className="px-3 py-3 text-slate-700 text-xs">{r.event_type || (r.has_signature !== undefined ? (r.has_signature ? "signed" : "unsigned") : "—")}</td>
                <td className="px-3 py-3 text-xs"><StatusPill status={r.status} /></td>
                <td className="px-3 py-3 text-right text-slate-700 text-xs whitespace-nowrap">
                  {r.amount_dollars != null ? fmt(r.amount_dollars) : r.body_size != null ? `${fmtN(r.body_size)} B` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabShell>
  );
}

function TestAccountsTab({ customers, saving, onToggle }) {
  const sorted = [...customers].sort((a, b) => {
    const at = a.is_test_account ? 1 : 0;
    const bt = b.is_test_account ? 1 : 0;
    if (at !== bt) return bt - at;
    return (a.email || "").localeCompare(b.email || "");
  });
  const flaggedCount = customers.filter(c => c.is_test_account).length;
  return (
    <TabShell
      title="Test Accounts"
      subtitle={`${flaggedCount} flagged. This flag is recorded on the user doc but does NOT yet affect any dashboard/burn-rate math (deferred). Use it to mark dummy/QA accounts for a future isolated metrics pass.`}
      count={customers.length}
      testId="test-accounts-tab-card"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pages Used</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Test Account?</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? <EmptyRow colSpan={4} text="No users." /> : sorted.map(c => (
              <tr key={c.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`test-accounts-row-${c.user_id}`}>
                <td className="px-6 py-3">
                  <p className="font-semibold text-slate-900">{c.center_name || c.name || "—"}</p>
                  <p className="text-slate-500 text-xs">{c.email}</p>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs">{c.plan_tier}</td>
                <td className="px-3 py-3 text-right text-slate-900 font-semibold">{fmtN(c.pages_used_year)}</td>
                <td className="px-3 py-3 text-center">
                  <button
                    type="button"
                    disabled={!!saving[c.user_id]}
                    onClick={() => onToggle(c.user_id, !!c.is_test_account)}
                    data-testid={`test-flag-toggle-${c.user_id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                      c.is_test_account
                        ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    } ${saving[c.user_id] ? "opacity-50" : ""}`}
                  >
                    {c.is_test_account ? <><Check className="w-3.5 h-3.5" /> Yes</> : <><XIcon className="w-3.5 h-3.5" /> No</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabShell>
  );
}

function DisclaimersTab({ data }) {
  if (!data) return <LoadingShell testId="disclaimers-loading" />;
  const rows = data.rows || [];
  return (
    <TabShell
      title="Terms & Conditions Acceptance"
      subtitle={`Accepted: ${fmtN(data.accepted || 0)} · Pending: ${fmtN(data.pending || 0)}`}
      count={data.total}
      testId="disclaimers-tab-card"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Accepted At</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <EmptyRow colSpan={4} text="No users." /> : rows.map(r => (
              <tr key={r.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`disclaimers-row-${r.user_id}`}>
                <td className="px-6 py-3">
                  <p className="font-semibold text-slate-900 flex items-center gap-1.5">
                    {r.center_name || r.name || "—"}
                    {r.is_test_account && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">TEST</span>}
                  </p>
                  <p className="text-slate-500 text-xs">{r.email}</p>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs">{r.account_type || r.role}</td>
                <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">{r.disclaimer_accepted_at ? new Date(r.disclaimer_accepted_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-3 text-xs">
                  {r.disclaimer_accepted_at
                    ? <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[11px] font-medium"><Check className="w-3 h-3" /> Accepted</span>
                    : <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2 py-0.5 rounded text-[11px] font-medium"><XIcon className="w-3 h-3" /> Pending</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabShell>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    ok: "bg-emerald-50 text-emerald-700",
    sent: "bg-emerald-50 text-emerald-700",
    success: "bg-emerald-50 text-emerald-700",
    paid: "bg-emerald-50 text-emerald-700",
    error: "bg-red-50 text-red-700",
    failed: "bg-red-50 text-red-700",
    no_api_key: "bg-amber-50 text-amber-700",
    duplicate_purchase: "bg-slate-100 text-slate-600",
    duplicate_plan_purchase: "bg-slate-100 text-slate-600",
    non_completed: "bg-slate-100 text-slate-600",
    no_link_found: "bg-amber-50 text-amber-700",
    raw_arrival: "bg-blue-50 text-blue-700",
  };
  const cls = map[s] || "bg-slate-100 text-slate-600";
  return <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${cls}`}>{status || "—"}</span>;
}

function KpiCard({ icon: Icon, label, value, subtitle, accent, small, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 ${accent.bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${accent.text}`} />
        </div>
      </div>
      <p className={`${small ? "text-lg" : "text-3xl"} font-bold text-slate-900`} style={{ fontFamily: "Manrope" }}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function AlertRow({ color, text }) {
  const dot = { red: "bg-red-500", orange: "bg-orange-500", amber: "bg-amber-400" }[color] || "bg-slate-400";
  return (
    <li className="flex items-center gap-2 text-slate-700">
      <span className={`w-2 h-2 rounded-full ${dot}`} /> {text}
    </li>
  );
}

function formatRelative(iso) {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} mo ago`;
    return `${Math.floor(days / 365)} yr ago`;
  } catch { return iso; }
}
